import React, { useEffect, useState } from "react";
import { Project, CreateProjectData } from "../../store/types";
import { useProjectsAPI } from "../../hooks/api/use-projectsAPI";
import { isNotEmpty, isAlphanumeric } from "../../hooks/validation";
import { useGetQuery } from "../../store/slices/apiSlice";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import {
  IconArrowLeft,
  IconId,
  IconIdFilled,
  IconCase,
  IconCaseFilled,
} from "../../assets/icons/Icon";
import { H2, DangerText } from "../../styles/Typography";
import { Container } from "../../components/Container/Container";
import {
  Flex,
  ButtonLabel,
  GoBackButton,
  ButtonContainer,
  Spinner,
} from "./CRUDPages.styles";

const ProjectFormPage: React.FC = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get("mode") || "create";
  const projectId = searchParams.get("id");
  const [loading, setLoading] = useState(false);

  const {
    projectsEP,
    handlePostProject,
    handleUpdateProject,
    handleDeleteProject,
    refetchData,
    toastRef,
  } = useProjectsAPI();

  const [formData, setFormData] = useState<CreateProjectData>({
    id: "",
    name: "",
  });
  const [errors, setErrors] = useState<
    Record<keyof CreateProjectData, boolean>
  >({
    id: false,
    name: false,
  });
  const [validForm, setValidForm] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);

  const {
    data: project,
    refetch: refetchProject,
    isLoading,
  } = useGetQuery(`${projectsEP}/${projectId}`, {
    refetchOnMountOrArgChange: true,
    skip: !projectId,
  });

  useEffect(() => {
    if (mode !== "create" && isLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [project, isLoading]);

  useEffect(() => {
    if (mode !== "create" && projectId && project) {
      const newData = {
        id: project.id || "",
        name: project.name || "",
      };
      setFormData(newData);
      const newErrors: Record<keyof CreateProjectData, boolean> = {
        id: !isAlphanumeric(newData.id),
        name: !isNotEmpty(newData.name),
      };
      setErrors(newErrors);
    } else {
      setFormData({
        id: "",
        name: "",
      });
      setErrors({
        id: false,
        name: false,
      });
    }
  }, [projectId, mode, project]);

  useEffect(() => {
    if (isMobile === null) return;

    if (!isMobile) {
      navigate("/projects", { replace: true });
    }
  }, [isMobile, navigate]);

  const handleFieldChange = (field: keyof CreateProjectData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateAllFields = (): boolean => {
    const newErrors: Record<keyof CreateProjectData, boolean> = {
      id: !isAlphanumeric(formData.id),
      name: !isNotEmpty(formData.name),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((err) => !err);
  };

  const handleIdBlur = (field: keyof CreateProjectData) => {
    const value = formData[field];
    const isValid = isAlphanumeric(value);
    setErrors((prev) => ({ ...prev, [field]: !isValid }));
  };

  const handleBlur = (field: keyof CreateProjectData) => {
    const value = formData[field];
    const isValid = isNotEmpty(value);
    setErrors((prev) => ({ ...prev, [field]: !isValid }));
  };

  const handleSubmit = async () => {
    if (mode !== "delete")
      if (!validateAllFields()) {
        setValidForm(false);
        return;
      }

    setValidForm(true);
    setSubmitting(true);
    let success = false;

    try {
      if (mode === "create") {
        success = await handlePostProject(formData);
      } else if (mode === "edit") {
        success = await handleUpdateProject({ ...formData, id: project!.id });
      } else if (mode === "delete") {
        success = await handleDeleteProject(project!);
      }
      if (success) {
        refetchData();
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else return false;
    } finally {
      setSubmitting(false);
    }
  };

  const getHeader = () => {
    switch (mode) {
      case "create":
        return "Новый проект";
      case "edit":
        return "Редактировать проект";
      case "delete":
        return "Удалить проект";
      default:
        return "Ошибка";
    }
  };

  const renderForm = () => (
    <>
      <CustomInputText
        value={formData.id}
        setValue={(val) => handleFieldChange("id", val)}
        onBlur={() => handleIdBlur("id")}
        placeholder="id"
        icon={<IconId size={20} />}
        invalid={errors.id}
      />
      <CustomInputText
        value={formData.name}
        setValue={(val) => handleFieldChange("name", val)}
        onBlur={() => handleBlur("name")}
        placeholder="Название"
        icon={<IconCase size={20} />}
        invalid={errors.name}
      />
    </>
  );

  const renderDelete = () => (
    <>
      <DangerText>Вы уверены, что хотите удалить данный проект?</DangerText>
      <StyledIconLine
        icon={<IconIdFilled size={20} />}
        text={project?.id ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconCaseFilled size={20} />}
        text={project?.name ?? "Нет данных"}
      />
    </>
  );

  if (loading)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  return (
    <Container>
      <Toast ref={toastRef} position="bottom-center" />
      <H2>{getHeader()}</H2>
      {validForm ? (
        <></>
      ) : (
        <DangerText>Пожалуйста, заполните все поля данных.</DangerText>
      )}
      {mode === "delete" ? renderDelete() : renderForm()}
      <Flex>
        <GoBackButton>
          <Button outlined onClick={() => navigate(-1)}>
            <ButtonLabel>
              <IconArrowLeft size={16} />
              Назад
            </ButtonLabel>
          </Button>
        </GoBackButton>
        <ButtonContainer>
          <Button
            label={
              mode === "delete"
                ? "Удалить"
                : mode === "edit"
                ? "Сохранить"
                : "Создать"
            }
            onClick={handleSubmit}
            loading={submitting}
          />
        </ButtonContainer>
      </Flex>
    </Container>
  );
};

export default ProjectFormPage;
